/**
 * The warning popover's adapter (issue #146): the page's grid in, the popover's issues out, and the
 * same cases as review items when Gjennomgå is pressed. No markup. The badge counts the list it
 * opens, so the two cannot disagree. Pure; tested in warningPopoverAdapter.test.ts.
 */
import type { ApiShift } from '@/api/data';
import { T } from '@/i18n';
import { type Entry, PERSON_CODES, oslo, ruleText } from '@/rules';
import { DN, fmtDs, fullName, num } from '@/ui/format';
import { dow, isoWeek } from '@timefly/core';
import type { ReviewItem } from './Panel';
import { type Grid, type Row, dateOf } from './model';
import type { Cat, Evidence, Issue, IssueAction, Scope } from './warningPopover.types';

export type { Cat, Issue, Scope } from './warningPopover.types';

export const CATS: [Cat, string][] = [
  ['arbeidstid', 'Arbeidstid'],
  ['hviletid', 'Hviletid'],
  ['pauser', 'Pauser'],
  ['registrert', 'Registrert tid'],
  ['annet', 'Annet'],
];
/** A rule code's group, by what the manager would change: the hours, the rest, the break, the punches. */
export function catOf(code: string): Cat {
  if (/lunch|break/.test(code)) return 'pauser';
  if (/rest/.test(code)) return 'hviletid';
  if (/^punch_|^open$/.test(code)) return 'registrert';
  if (/over|youth_night|youth_evening|sundays/.test(code)) return 'arbeidstid';
  return 'annet';
}

export interface AdapterContext {
  grid: Pick<Grid, 'rows' | 'open' | 'entries'>;
  /** the days on screen */
  days: string[];
  /** every shift the page holds, the day before the window included: a rest rule's other shift */
  shifts: ApiShift[];
  t: (k: string, v?: Record<string, string | number>) => string;
  /** whether this reader may change shifts: a correction, or a look */
  editable: boolean;
  /** whether this reader may open the employee card, where a missing birth date is filled in */
  canPerson: boolean;
  /** whether this reader may open Innstillinger, where the company's rules and breaks are set */
  canSettings: boolean;
}

/** One rule entry in a scope, before it is worded. */
interface Raw {
  key: string;
  entry: Entry;
  who: string;
  when: string;
  shift?: ApiShift;
  person?: number;
}

const day = (d: string) => `${DN[dow(d)]} ${fmtDs(d)}`;
const clock = (iso: string | null | undefined) => oslo(iso)?.time ?? '';
/**
 * The planned interval, said as planned: the card shows clocked times once there are any, and a
 * popover that mixed the two without saying so would move the plan (WP-19). A shift that ends on
 * another date says which (WP-03).
 */
export function planned(s: ApiShift): string {
  const end = oslo(s.end_time)?.date ?? '';
  const t = `${clock(s.start_time)}–${clock(s.end_time)}`;
  return end && end !== dateOf(s) ? T('plan {t} (til {d})', { t, d: day(end) }) : T('plan {t}', { t });
}
const at = (d: string, s: ApiShift) => `${day(d)}, ${planned(s)}`;
/** Each entry on these shifts, once; an entry about the person (age unknown) once per list (#35). */
function shiftRaws(
  list: [string, ApiShift][],
  grid: AdapterContext['grid'],
  who: string,
  said = new Set<string>(),
) {
  return list.flatMap(([d, s]) =>
    (grid.entries.get(s.id) ?? []).flatMap((e, i): Raw[] => {
      if (PERSON_CODES.has(e.code)) {
        if (said.has(e.code)) return [];
        said.add(e.code);
      }
      return [{ key: `s${s.id}-${i}`, entry: e, who, when: at(d, s), shift: s }];
    }),
  );
}
const weekRaws = (r: Row): Raw[] =>
  r.week.entries.map((e) => ({
    key: `w${r.user.id}-${e.week ?? ''}-${e.code}`,
    entry: e,
    who: fullName(r.user),
    when: e.week ? T('Uke {n}', { n: isoWeek(e.week) }) : '',
    person: r.user.id,
  }));

function raws(scope: Scope, { grid, days }: AdapterContext): Raw[] {
  const cells = (r: Row, only?: string) =>
    days
      .filter((d) => !only || d === only)
      .flatMap((d) => (r.cells.get(d)?.shifts ?? []).map((s) => [d, s] as [string, ApiShift]));
  if (scope.kind === 'person') {
    const r = grid.rows.find((x) => x.user.id === scope.person);
    // the week first: it is the one that differs, and the one that matters most
    return r ? [...weekRaws(r), ...shiftRaws(cells(r), grid, fullName(r.user))] : [];
  }
  if (scope.kind === 'day') {
    const open = (grid.open.get(scope.day) ?? []).map((s) => [scope.day, s] as [string, ApiShift]);
    return [
      ...grid.rows.flatMap((r) => shiftRaws(cells(r, scope.day), grid, fullName(r.user))),
      ...shiftRaws(open, grid, T('Ledig vakt')),
    ];
  }
  for (const r of grid.rows)
    for (const [d, s] of cells(r))
      if (s.id === scope.shiftId) return shiftRaws([[d, s]], grid, fullName(r.user));
  for (const d of days)
    for (const s of grid.open.get(d) ?? [])
      if (s.id === scope.shiftId) return shiftRaws([[d, s]], grid, T('Ledig vakt'));
  return [];
}

/** What the limit is a limit of: rest is a minimum, the rest maxima. Null when the rule sent none. */
export function limitText(rule: Entry): string | null {
  const max = Number(rule.args?.max ?? 0);
  if (!(max > 0)) return null;
  return rule.code === 'weekrest'
    ? T('minst {h} t sammenhengende fri', { h: num(max) })
    : rule.code.endsWith('_4w')
      ? T('{h} t overtid på fire uker', { h: num(max) })
      : rule.code.endsWith('_avg')
        ? T('{h} t i snitt over åtte uker', { h: num(max) })
        : rule.week
          ? T('{h} t i uka', { h: num(max) })
          : T('{h} t', { h: num(max) });
}
/**
 * What the rule was counted on, where that is known: the plan on screen (core), the punches (the
 * punch tolerance), or the server's week on the plan or the punches. A daily server entry does not
 * say which it read, so it gets no line rather than a guess (WP-19).
 */
export function basisText(rule: Entry): string | null {
  if (rule.source === 'core') return T('Planen som vises her');
  if (rule.code.startsWith('punch_')) return T('Stemplet tid mot planlagt tid');
  return rule.label === 'Actual'
    ? // the server's actual week: punches where there are any, the plan where nobody clocked,
      // less the break (admin/shiftController.js calculateWorkHours)
      T('Stemplet tid, planen der ingen har stemplet, etter pause')
    : rule.label === 'Planned'
      ? T('Planlagt tid etter pause')
      : null;
}

/** The person's shift that ended last before this one started, from what the page holds. */
function before(s: ApiShift, shifts: ApiShift[]): ApiShift | null {
  const start = Date.parse(s.start_time);
  return (
    shifts
      .filter(
        (x) =>
          x.id !== s.id && x.user_id != null && x.user_id === s.user_id && Date.parse(x.end_time) <= start,
      )
      .sort((a, b) => Date.parse(b.end_time) - Date.parse(a.end_time))[0] ?? null
  );
}

/**
 * What the rule measured, by code, from the payload the server sends (admin/shiftController.js):
 * `hours` is the rest, the worked day, the week or the overtime. A code not listed gets no actual
 * row; its sentence says what it says.
 */
function actualOf(e: Entry): Evidence | null {
  const h = Number(e.args?.hours ?? 0);
  const max = Number(e.args?.max ?? 0);
  const v = (x: number) => T('{h} t', { h: num(Math.round(x * 10) / 10) });
  if (/^(rest|youth_rest|weekrest)$/.test(e.code))
    return { label: T('Lengste sammenhengende fri'), value: v(h) };
  if (/^(day_over|day_over_max|day_over_13|youth_day_over)$/.test(e.code))
    return { label: T('Arbeidstid'), value: v(h) };
  // the server sends the hours over the limit for this one; the week is the two together (RulePanel)
  if (e.code === 'weekly_over' && max > 0) return { label: T('Uka slik den er regnet'), value: v(h + max) };
  if (e.code === 'weekly_over_48') return { label: T('Arbeidstid i uka'), value: v(h) };
  if (/^(weekly_over_cap|weekly_over_max|weekly_over_4w|year_over_200)$/.test(e.code))
    return { label: T('Overtid'), value: v(h) };
  return null;
}
/**
 * Limits the server does not send but holds as constants (admin/shiftController.js): REST_HOURS
 * 11, the four-week overtime check `fourWeeks > 25`. Anything else without `max` is unknown and says
 * so (WP-17).
 */
const FIXED: Record<string, () => string> = {
  rest: () => T('minst {h} t sammenhengende fri', { h: 11 }),
  weekly_over_4w: () => T('{h} t overtid på fire uker', { h: 25 }),
};
const SOURCED: Record<string, string> = {
  day_over: 'Avtalt daglig arbeidstid',
  day_over_max: 'Normalarbeidstid per dag',
};

/**
 * The values the rule compared, measured value and limit first, then the context that places them
 * (review of 208ee199: the figures were below the fold behind repeated rows). Who and when is left
 * out in a shift's own popover, whose header already says it. Nothing filled in by guess.
 */
function evidenceOf(r: Raw, shifts: ApiShift[], scope: Scope): Evidence[] {
  const e = r.entry;
  const s = r.shift;
  const out: Evidence[] = [];
  if (s && (e.code.startsWith('punch_in') || e.code === 'open')) {
    if (s.punch_in_time) out.push({ label: T('Stemplet inn'), value: clock(s.punch_in_time) });
    out.push({ label: T('Planlagt start'), value: clock(s.start_time) });
  }
  if (s && (e.code.startsWith('punch_out') || e.code === 'open')) {
    out.push({ label: T('Stemplet ut'), value: s.punch_out_time ? clock(s.punch_out_time) : T('mangler') });
    out.push({ label: T('Planlagt slutt'), value: clock(s.end_time) });
  }
  if (!PERSON_CODES.has(e.code)) {
    const actual = actualOf(e);
    if (actual) out.push(actual);
    out.push({
      label: T('Grense'),
      value: limitText(e) ?? FIXED[e.code]?.() ?? T('Ikke sendt med varselet'),
    });
  }
  // the shift before a rest rule, as planned; this shift is in the header or in Gjelder
  const prev = s && /rest/.test(e.code) ? before(s, shifts) : null;
  if (prev) out.push({ label: T('Vakten før'), value: at(dateOf(prev), prev) });
  if (scope.kind !== 'shift' || !s)
    out.push({ label: T('Gjelder'), value: [r.who, r.when].filter(Boolean).join(', ') });
  const basis = PERSON_CODES.has(e.code) ? null : basisText(e);
  if (basis) out.push({ label: T('Regnet på'), value: basis });
  return out;
}

/**
 * Where the rule and its limit come from, one wording for one source: a constant in Timefly, the
 * person's agreed hours, the plan on screen, or the server's check with nothing more said. No
 * company configuration is implied where the product fixed the limit (review of 208ee199).
 */
function sourceOf(e: Entry): string {
  if (PERSON_CODES.has(e.code)) return T('Mangler på ansattkortet');
  if (e.source === 'core') return T('Sjekket i planen her');
  if (!limitText(e) && FIXED[e.code]) return T('Fast grense i Timefly');
  if (limitText(e) && SOURCED[e.code]) return T(SOURCED[e.code] as string);
  return T('Sjekket av serveren');
}

/** Where the issue is fixed, for this reader: a correction where they may make it, a look where not. */
function actionOf(r: Raw, ctx: AdapterContext): [IssueAction, string] {
  const s = r.shift;
  if (!s)
    return [{ kind: 'week', person: r.person ?? 0, code: r.entry.code, week: r.entry.week }, T('Se uka')];
  const code = r.entry.code;
  // a missing birth date is filled in on the employee card (WP-17), for whoever may open it
  if (PERSON_CODES.has(code) && ctx.canPerson && s.user_id != null)
    return [{ kind: 'person', person: s.user_id }, T('Åpne medarbeideren')];
  if (/lunch|break/.test(code)) return [{ kind: 'lunch', shiftId: s.id }, T('Se pausen i vakten')];
  if (!ctx.editable) return [{ kind: 'view', shiftId: s.id }, T('Åpne vakten')];
  if (code.startsWith('punch_in'))
    return [{ kind: 'punch', shiftId: s.id, field: 'in' }, T('Rett stemplet inn')];
  if (code.startsWith('punch_out') || code === 'open')
    return [{ kind: 'punch', shiftId: s.id, field: 'out' }, T('Rett stemplet ut')];
  return [{ kind: 'edit', shiftId: s.id }, T('Endre vakten')];
}

/** The issues in one scope, on the days on screen, worded and ready to draw. */
export function issuesOf(scope: Scope, ctx: AdapterContext): Issue[] {
  return raws(scope, ctx).map((r) => {
    const e = r.entry;
    const groupKey = `timeline.group_${e.code}`;
    const [action, actionLabel] = actionOf(r, ctx);
    return {
      key: r.key,
      cat: catOf(e.code),
      sev: e.sev,
      text: ruleText(ctx.t, e),
      heading: ctx.t(groupKey) === groupKey ? ruleText(ctx.t, e) : ctx.t(groupKey),
      description: `${e.sev === 'd' ? T('Brudd') : T('Advarsel')}: ${ruleText(ctx.t, e)}`,
      who: r.who,
      when: r.when,
      evidence: evidenceOf(r, ctx.shifts, scope),
      source: sourceOf(e),
      note: T('Å lese varselet endrer ikke planen, og det stopper ikke publisering.'),
      action,
      actionLabel,
      // the rule sent no limit and Timefly holds none: the company's own rules are where it is set
      ...(ctx.canSettings && !PERSON_CODES.has(e.code) && !limitText(e) && !FIXED[e.code]
        ? { setup: { label: T('Se reglene i Innstillinger'), to: '/innstillinger/regler' } }
        : {}),
    };
  });
}

/**
 * The cases a popover's Gjennomgå carries into the review: exactly the keys it showed, so the count
 * on the button and the list it opens agree (WP-07). A case resolved since is simply gone.
 */
export function reviewItemsOf(scope: Scope, keys: string[], ctx: AdapterContext): ReviewItem[] {
  const want = new Set(keys);
  return raws(scope, ctx)
    .filter((r) => want.has(r.key))
    .map(
      (r): ReviewItem => ({
        key: r.key,
        group: r.entry.code === 'open' ? 'attend' : 'rules',
        sev: r.entry.sev,
        who: r.shift ? `${r.who}, ${DN[dow(dateOf(r.shift))]} ${fmtDs(dateOf(r.shift))}` : r.who,
        text: ruleText(ctx.t, r.entry),
        ...(r.shift ? { shiftId: r.shift.id, code: r.entry.code } : { person: r.person ?? 0, rule: r.entry }),
      }),
    );
}
